/**
 * Client-side Destination model with custom methods and computed properties
 */

import type { WidgetImage } from '@elara/domain/models/schemas/widget-schemas';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { MapPoint } from '@elara/client-core/ui-contracts/map-point';

import { DestinationModelSpec } from '@elara/domain/models/DestinationModelSpec';

import { createClientModel } from '#firebase/createClientModel.js';

/**
 * Destination model for client-side usage
 * Provides reactive access to destination data with computed properties
 */
export function createClientDestinationModel(firestore: FirestoreContainer) {
  const model = createClientModel(
    firestore,
    DestinationModelSpec,
    () => ({
      /** Display name for the destination */
      get displayName(): string {
        return this.name || 'Unknown Destination';
      },

      // NOTE: `countryName` and `isActive` are real schema fields (DestinationSchema), so they are NOT
      // redefined as computed getters here — a getter with the same name shadows the data field, making
      // it getter-only, which crashes the reactive instance when the field is (re)hydrated from a
      // snapshot ("Cannot assign to property 'value' which has only a getter"). Read the fields directly.

      /** Address for the destination */
      get address(): string {
        return `${this.name}, ${this.location?.stateProvince}, ${this.location?.countryName}`;
      },

      /** Images for the gallery widget */
      get imagesForGallery(): WidgetImage[] {
        return (
          this.images?.map((image, index) => ({
            id: `${this.id}-image-${index}`,
            url: image.url,
            caption: image.caption,
            attribution: image.attribution?.credit ?? undefined,
            alt: image.caption,
          })) ?? []
        );
      },

      /** Image URL for the destination */
      get imageUrl(): string | undefined {
        return this.images?.[0]?.url;
      },

      /** Image URLs for the destination */
      get imageUrls(): string[] {
        return this.images?.map((image) => image.url).filter(Boolean) as string[];
      },

      get mapPoint(): MapPoint {
        const point: MapPoint = {
          id: this.id,
          label: this.displayName,
          latitude: this.coordinates?.lat,
          longitude: this.coordinates?.lng,
          address: this.address,
        };

        return point;
      },
    }),
    {
      // No flows attached - destinations are read-only for now
      // Future: could add flows for creating/updating destinations if needed
    },
  );
  return model;
}

export type ClientDestinationModel = ReturnType<typeof createClientDestinationModel>;
export type Destination = ClientInstanceOf<ClientDestinationModel>;
